import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  Edit3,
  Sparkles,
  MessageSquare,
  Brain,
  CheckCircle,
  ArrowRight,
  Layers,
  Target,
  Lightbulb,
  ChevronRight,
  PenTool,
  Zap
} from 'lucide-react';

const EssayDemo = ({ data }) => {
  const [activePhase, setActivePhase] = useState('freewriting');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => setShowAnalysis(true), 1500);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  // Typewriter effect for freewriting
  useEffect(() => {
    if (activePhase === 'freewriting' && isInView) {
      const text = "The pedestrian bridge collapse last year hit our community hard. As I stood there watching the emergency crews, I realized this wasn't just about infrastructure failure - it was about trust. That's when I knew civil engineering wasn't just about building things...";
      let index = 0;
      const timer = setInterval(() => {
        if (index < text.length) {
          setTypewriterText(text.substring(0, index + 1));
          index++;
        } else {
          clearInterval(timer);
        }
      }, 30);
      return () => clearInterval(timer);
    }
  }, [activePhase, isInView]);

  const essayPhases = {
    freewriting: {
      icon: MessageSquare,
      title: 'Freewriting',
      description: 'Capture authentic voice without judgment',
      content: typewriterText || 'Start writing...'
    },
    structuring: {
      icon: Layers,
      title: 'Structure Builder',
      description: 'Organize ideas into compelling narrative',
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-azure-50 rounded-lg border border-azure-200">
            <div className="text-sm font-medium text-slate-900 mb-1">Hook: Personal Connection</div>
            <div className="text-xs text-slate-600">Bridge collapse witness → engineering as community service</div>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="text-sm font-medium text-slate-900 mb-1">Body: Technical + Human</div>
            <div className="text-xs text-slate-600">City planning experience → infrastructure affects lives</div>
          </div>
          <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
            <div className="text-sm font-medium text-slate-900 mb-1">Conclusion: MIT Vision</div>
            <div className="text-xs text-slate-600">Research goals → sustainable urban infrastructure</div>
          </div>
        </div>
      )
    },
    revision: {
      icon: Edit3,
      title: 'Smart Revision',
      description: 'AI-powered feedback and refinement',
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-yellow-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-slate-900">Strengthen Opening</div>
                <div className="text-xs text-slate-600">Consider starting with dialogue or sensory detail</div>
              </div>
            </div>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-slate-900">Excellent Detail</div>
                <div className="text-xs text-slate-600">City planning experience is unique and specific</div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 rounded-full mb-4">
          <FileText className="w-4 h-4 text-indigo-600" />
          <span className="text-sm font-medium text-indigo-700">
            Essay Development System
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          From Raw Ideas to Polished Narrative
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Our three-phase process preserves authentic voice while crafting essays
          that resonate with admissions committees. Every word serves your strategic narrative.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Essay Development Phases */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200">
            {/* Phase Tabs */}
            <div className="flex border-b border-slate-200">
              {Object.entries(essayPhases).map(([key, phase]) => {
                const Icon = phase.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActivePhase(key)}
                    className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 transition-all
                      ${activePhase === key
                        ? 'bg-indigo-50 border-b-2 border-indigo-600 text-indigo-700'
                        : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{phase.title}</span>
                  </button>
                );
              })}
            </div>

            {/* Phase Content */}
            <div className="p-6">
              <div className="mb-4">
                <h3 className="font-semibold text-slate-900 mb-2">
                  {essayPhases[activePhase].title}
                </h3>
                <p className="text-sm text-slate-600">
                  {essayPhases[activePhase].description}
                </p>
              </div>

              {/* Essay Content Area */}
              <div className="min-h-[300px] p-4 bg-slate-50 rounded-lg border border-slate-200">
                {typeof essayPhases[activePhase].content === 'string' ? (
                  <div className="font-serif text-slate-800 leading-relaxed">
                    {essayPhases[activePhase].content}
                    {activePhase === 'freewriting' && typewriterText.length > 0 && (
                      <span className="animate-pulse">|</span>
                    )}
                  </div>
                ) : (
                  essayPhases[activePhase].content
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">MIT Supplemental Essay</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                    Draft v3
                  </span>
                </div>
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Get AI Feedback
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: AI Analysis & Tools */}
        <div className="space-y-4">
          {/* Essay Analysis */}
          {showAnalysis && (
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6"
              style={{
                animation: 'fadeInScale 0.6s ease-out forwards'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-slate-900">AI Analysis</h3>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-600">Authenticity</span>
                    <span className="text-sm font-bold text-emerald-600">92%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: '92%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-600">Specificity</span>
                    <span className="text-sm font-bold text-azure-600">88%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-azure-500" style={{ width: '88%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-600">MIT Alignment</span>
                    <span className="text-sm font-bold text-violet-600">95%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500" style={{ width: '95%' }} />
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="text-xs font-medium text-emerald-700">
                  Strong Match: Engineering + community impact theme aligns perfectly with MIT values
                </div>
              </div>
            </div>
          )}

          {/* Smart Suggestions */}
          <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl border border-indigo-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-900">Smart Tools</h3>
            </div>

            <div className="space-y-2">
              <button className="w-full text-left p-3 bg-white rounded-lg border border-slate-200 hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-azure-500" />
                    <span className="text-sm font-medium text-slate-900">Hook Generator</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-xs text-slate-600 mt-1">Create compelling openings</div>
              </button>

              <button className="w-full text-left p-3 bg-white rounded-lg border border-slate-200 hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PenTool className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-medium text-slate-900">Voice Calibrator</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-xs text-slate-600 mt-1">Maintain authentic tone</div>
              </button>

              <button className="w-full text-left p-3 bg-white rounded-lg border border-slate-200 hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-medium text-slate-900">Theme Connector</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-xs text-slate-600 mt-1">Link to other essays</div>
              </button>
            </div>
          </div>

          {/* Progress Tracker */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Essay Progress</h3>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-700">Common App Essay</span>
                <span className="ml-auto text-xs text-emerald-600 font-medium">Complete</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-azure-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-azure-500 rounded-full" />
                </div>
                <span className="text-sm text-slate-700">MIT Essays</span>
                <span className="ml-auto text-xs text-azure-600 font-medium">In Progress</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                <span className="text-sm text-slate-700">Caltech Essays</span>
                <span className="ml-auto text-xs text-slate-500">Not Started</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default EssayDemo;